import { create } from 'zustand'
import { debugUtils } from '@/config/app'

export interface InitializeSessionProps {
  token: string
  refreshToken: string
}

interface UserAuthSessionState {
  token: string | null
  refreshToken: string | null
  isSessionOpen: boolean

  initializeSession: (props: InitializeSessionProps) => Promise<void>
  removeSession: () => Promise<void>
  restoreSession: () => Promise<void>
}

// Constants for localStorage keys (compatible with mobile app)
const STORAGE_KEYS = {
  TOKEN: 'auth_token',
  REFRESH_TOKEN: 'auth_refresh_token',
  SESSION_OPEN: 'session_open',
} as const

// Helper functions for localStorage operations
const saveItem = async (key: string, value: string): Promise<void> => {
  localStorage.setItem(key, value)
}

const getItem = async (key: string): Promise<string | null> => {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

const deleteItem = async (key: string): Promise<void> => {
  localStorage.removeItem(key)
}

export const useUserAuthSessionStore = create<UserAuthSessionState>((set) => ({
  token: null,
  refreshToken: null,
  isSessionOpen: false,

  restoreSession: async () => {
    try {
      debugUtils.authLog('🔄 Restoring auth session from localStorage...')

      const [token, refreshToken, sessionFlag] = await Promise.all([
        getItem(STORAGE_KEYS.TOKEN),
        getItem(STORAGE_KEYS.REFRESH_TOKEN),
        getItem(STORAGE_KEYS.SESSION_OPEN),
      ])

      debugUtils.authLog('📦 Restored session data:', {
        hasToken: !!token,
        hasRefreshToken: !!refreshToken,
        sessionFlag,
        tokenLength: token?.length || 0,
        refreshTokenLength: refreshToken?.length || 0,
      })

      set({
        token,
        refreshToken,
        isSessionOpen: sessionFlag === 'true',
      })
    } catch (error) {
      // On error, set a safe state
      set({
        token: null,
        refreshToken: null,
        isSessionOpen: false,
      })
    }
  },

  initializeSession: async ({ token, refreshToken }) => {
    debugUtils.authLog('🔐 Initializing new auth session...', {
      tokenLength: token.length,
      refreshTokenLength: refreshToken.length,
    })

    // First save to localStorage synchronously
    await Promise.all([
      saveItem(STORAGE_KEYS.TOKEN, token),
      saveItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken),
      saveItem(STORAGE_KEYS.SESSION_OPEN, 'true'),
    ])

    debugUtils.authLog('✅ Session tokens saved to localStorage')

    // Then update the state
    set({
      token,
      refreshToken,
      isSessionOpen: true,
    })

    debugUtils.authLog('✅ Session state updated')
  },

  removeSession: async () => {
    debugUtils.authLog('🗑️ Removing auth session...')

    // First clear localStorage
    await Promise.all([
      deleteItem(STORAGE_KEYS.TOKEN),
      deleteItem(STORAGE_KEYS.REFRESH_TOKEN),
      saveItem(STORAGE_KEYS.SESSION_OPEN, 'false'),
    ])

    debugUtils.authLog('✅ Session data cleared from localStorage')

    // Then clear the state
    set({
      token: null,
      refreshToken: null,
      isSessionOpen: false,
    })

    debugUtils.authLog('✅ Session state cleared')
  },
}))
